import { Flex, Input } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { CustomMedicationDto } from '../../../lib/ServerApi';
import { api } from '../../../lib/api';
import { Page } from '../../../components/layout/page';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { LinkButton } from '@/components/ui/link-button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { toaster } from '@/components/ui/toaster';

const validationSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    dose: z.string().min(1, 'Dose is required'),
    doseUnit: z.string().min(1, 'Unit is required'),
    route: z.string().min(1, 'Route is required'),
    active: z.boolean(),
});

type MedicationFormValues = z.infer<typeof validationSchema>;

export function MedicationForm() {
    const navigate = useNavigate();
    const params = useParams<'medicationId'>();
    const queryClient = useQueryClient();
    const cached = queryClient.getQueryData<CustomMedicationDto>(['custom-medication', params.medicationId]);

    const rhf = useForm<MedicationFormValues>({
        defaultValues: {
            name: '',
            dose: '',
            doseUnit: '',
            route: '',
            active: true,
            ...cached,
        },
        resolver: zodResolver(validationSchema),
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const { mutate, isPending } = useMutation<CustomMedicationDto, Error, MedicationFormValues>({
        mutationFn: async (values) => {
            if (!params.medicationId) {
                throw new Error('Medication ID is required');
            }

            const response = await api.customMedication.update(params.medicationId, values);
            return response.data;
        },
        onSuccess: () => {
            navigate('/settings/medications');
        },
        onError: () => {
            toaster.create({
                title: 'Failed to update medication',
                type: 'error',
            });
        },
    });

    return (
        <Page>
            <form noValidate onSubmit={rhf.handleSubmit((values) => mutate(values))}>
                <Flex gap="8" flexDirection="column" width="100%" css={{ '--field-label-width': 0 }}>
                    <Card>
                        <FieldGroup>
                            <FieldRow>
                                <Field
                                    label="Medication Name"
                                    required
                                    invalid={!!rhf.formState.errors.name}
                                    errorText={rhf.formState.errors.name?.message}>
                                    <Input type="text" {...rhf.register('name')} />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field
                                    label="Dose"
                                    required
                                    invalid={!!rhf.formState.errors.dose}
                                    errorText={rhf.formState.errors.dose?.message}
                                    flex="0 0 140px">
                                    <Controller
                                        name="dose"
                                        control={rhf.control}
                                        render={({ field }) => (
                                            <NumberInputRoot
                                                size="sm"
                                                min={0}
                                                name={field.name}
                                                value={`${field.value}`}
                                                onValueChange={({ value }) => field.onChange(value)}>
                                                <NumberInputField onBlur={field.onBlur} bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                            </NumberInputRoot>
                                        )}
                                    />
                                </Field>
                                <Field
                                    label="Unit"
                                    required
                                    invalid={!!rhf.formState.errors.doseUnit}
                                    errorText={rhf.formState.errors.doseUnit?.message}
                                    flex="0 0 120px">
                                    <Input type="text" {...rhf.register('doseUnit')} />
                                </Field>
                                <Field
                                    label="Route"
                                    required
                                    invalid={!!rhf.formState.errors.route}
                                    errorText={rhf.formState.errors.route?.message}>
                                    <Input type="text" {...rhf.register('route')} />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Status">
                                    <Controller
                                        name="active"
                                        control={rhf.control}
                                        render={({ field: { value, onChange, ...props } }) => (
                                            <Switch {...props} checked={value} onCheckedChange={({ checked }) => onChange(checked)}>
                                                {value ? 'Active' : 'Inactive'}
                                            </Switch>
                                        )}
                                    />
                                </Field>
                            </FieldRow>
                        </FieldGroup>
                    </Card>

                    <Flex alignItems="flex-start" gap="4">
                        <Button type="submit" loading={isPending}>
                            Save
                        </Button>
                        <LinkButton to="/settings/medications">Cancel</LinkButton>
                    </Flex>
                </Flex>
            </form>
        </Page>
    );
}
